import { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface Props {
    children: ReactNode;
    fallback: (props: { error: Error; resetErrorBoundary: () => void }) => ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    didCatch: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        didCatch: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { didCatch: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.props.onError?.(error, errorInfo);
    }

    private handleReset = () => {
        this.setState({ didCatch: false, error: null });
    };

    public render() {
        if (this.state.didCatch) {
            if (this.props.fallback) {
                return this.props.fallback({ error: this.state.error!, resetErrorBoundary: this.handleReset });
            }

            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "200px",
                        p: 3,
                        textAlign: "center",
                    }}
                >
                    <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="h5" color="error" gutterBottom>
                        Something went wrong
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {this.state.error?.message || "An unexpected error occurred"}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={this.handleReset}
                    >
                        Try again
                    </Button>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary; 